import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import cuid from 'cuid';
import TimeMe from 'timeme.js';

import { socketSelector } from 'containers/App/selectors';
import { IS_MOBILE } from 'utils/constants';

import { connectToChannel, leaveChannel, trackEvent } from './actions';
import { startView, endView, eventPayload } from './utils/helpers';

const VISIBILITYCHANGE = 'visibilitychange';
const BEFOREUNLOAD = 'beforeunload';
const IDLE_TIMEOUT_SECONDS = 30;
let UNLOADING = false;

const AnalyticsComponent = (props) => {
  const {
    history,
    location,
    socket,
    handleConnectToChannel,
    handleLeaveChannel,
    handleTrackEvent,
  } = props;
  const eventRef = useRef(null);
  const prevLocationRef = useRef({ pathname: '' });
  const [channel, setChannel] = useState(null);

  const sendEvent = useCallback(
    (async, end) => {
      if (!eventRef.current) {
        return;
      }
      const duration = TimeMe.getTimeOnCurrentPageInSeconds();

      if (duration >= eventRef.current.minDuration) {
        const data = {
          ...eventPayload(eventRef.current),
          duration: parseInt(duration, 10),
          end: end || UNLOADING,
          is_mobile: IS_MOBILE,
        };
        handleTrackEvent(channel, data, async, eventRef.current.public);
      }
    },
    [channel, handleTrackEvent]
  );

  const onRouteChange = useCallback(
    (currLocation) => {
      const prevLocation = prevLocationRef.current;
      const newEvent = startView(prevLocation, currLocation);

      if (prevLocation.pathname === currLocation.pathname) {
        return;
      }

      if (!eventRef.current && newEvent) {
        TimeMe.resetAllRecordedPageTimes();
        TimeMe.startTimer();
        eventRef.current = {
          ...newEvent,
          cuid: cuid(),
          started_at: moment.utc().format(),
        };
        setTimeout(() => sendEvent(false, false), newEvent.minDuration * 1000);
      } else if (
        eventRef.current &&
        endView(eventRef.current, prevLocation, currLocation)
      ) {
        sendEvent(false, true);

        if (newEvent) {
          TimeMe.resetAllRecordedPageTimes();
          TimeMe.startTimer();
          eventRef.current = {
            ...newEvent,
            cuid: cuid(),
            started_at: moment.utc().format(),
          };
          setTimeout(
            () => sendEvent(false, false),
            newEvent.minDuration * 1000
          );
        } else {
          TimeMe.resetAllRecordedPageTimes();
          eventRef.current = null;
        }
      }

      prevLocationRef.current = currLocation;
    },
    [sendEvent]
  );

  useEffect(() => {
    TimeMe.initialize({ idleTimeoutInSeconds: IDLE_TIMEOUT_SECONDS });
  }, []);

  useEffect(() => {
    if (socket && !channel) {
      handleConnectToChannel(socket).then(setChannel);
    } else if (!socket && channel) {
      setChannel(null);
    }
  }, [socket, handleConnectToChannel, channel]);

  useEffect(
    () => () => {
      if (channel) {
        handleLeaveChannel(channel);
      }
    },
    [handleLeaveChannel, channel]
  );

  useEffect(() => {
    const unlisten = history.listen((currLocation) => {
      onRouteChange(currLocation);
    });

    return unlisten;
  }, [history, onRouteChange]);

  useEffect(() => {
    onRouteChange(location);
  }, [onRouteChange, location]);

  useEffect(() => {
    const visibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendEvent(true, false);
      }
    };
    const beforeUnload = () => {
      UNLOADING = true;
    };

    document.addEventListener(VISIBILITYCHANGE, visibilityChange);
    window.addEventListener(BEFOREUNLOAD, beforeUnload);

    return () => {
      document.removeEventListener(VISIBILITYCHANGE, visibilityChange);
      window.removeEventListener(BEFOREUNLOAD, beforeUnload);
    };
  }, [sendEvent]);

  return null;
};

AnalyticsComponent.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  socket: PropTypes.object,
  handleConnectToChannel: PropTypes.func,
  handleLeaveChannel: PropTypes.func,
  handleTrackEvent: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  socket: socketSelector,
});

function mapDispatchToProps(dispatch) {
  return {
    handleConnectToChannel: (socket) => dispatch(connectToChannel(socket)),
    handleLeaveChannel: (channel) => dispatch(leaveChannel(channel)),
    handleTrackEvent: (channel, event, async, isPublic) =>
      dispatch(trackEvent(channel, event, async, isPublic)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const Analytics = compose(withConnect, withRouter)(AnalyticsComponent);
